// src/services/videoPoster.js — the still that stands in for a video.
//
// 🟥 THE BROWSER HAS NEVER MADE ONE, AND EVERY SURFACE EXPECTS IT. The phone
//    grabs a frame a second into each clip, uploads it, and writes it to the
//    post's `imageUrl` — that field is the poster for a video post, and the
//    feed, the reels player and the moderation console all read it. The web's
//    createPost wrote `imageUrl: ''` for a video and moved on.
//
//    So a clip posted from a browser had no still ANYWHERE: a black rectangle
//    in the feed until it buffers, nothing behind the play button in the
//    console, and nothing at all in a list that only draws images.
//
// 🟢 SAME FRAME, SAME FIELD, SAME SHAPE AS THE PHONE'S — one second in, JPEG,
//    uploaded under `postThumbnails/`. Nothing downstream has to learn that
//    this one came from a browser.
//
// 🟥 AND IT IS FAIL-SOFT, DELIBERATELY. A codec the browser will not decode, a
//    tainted canvas, a video that never fires `seeked` — none of those may cost
//    somebody their post. Every failure returns null and the upload carries on
//    exactly as it did before this file existed.

/** How far into the clip to grab, in seconds. The very first frame of a phone
    video is very often black — the sensor is still settling. */
const AT_SECONDS = 1;
/* a poster is a thumbnail, not a second copy of the video's first frame at full
   sensor resolution — 720px wide is more than any surface draws it at */
const MAX_W = 720;

/**
 * Draw one frame of a video file into a JPEG blob.
 *
 * @param file  the File/Blob the composer is about to upload
 * @return a Blob, or null when this browser cannot produce one
 */
export function posterFromVideo(file) {
  return new Promise((resolve) => {
    let url = null;
    let settled = false;
    const done = (v) => {
      if (settled) return;
      settled = true;
      if (url) { try { URL.revokeObjectURL(url); } catch { /* already gone */ } }
      resolve(v);
    };

    try {
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      /* 🟥 WITHOUT THIS, A CROSS-ORIGIN FRAME TAINTS THE CANVAS and `toBlob`
         throws a SecurityError. The file is a local Blob here so it is
         same-origin, but the attribute costs nothing and makes the failure
         impossible rather than unlikely. */
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      /* 🟥 A HARD DEADLINE. A video element that never fires `loadeddata` — a
         codec the browser half-supports, a file that is still being written —
         would otherwise leave the composer waiting for ever on a thumbnail
         nobody asked for. */
      const timer = setTimeout(() => done(null), 8000);

      const fail = () => { clearTimeout(timer); done(null); };
      video.onerror = fail;

      video.onloadeddata = () => {
        /* seek past the settling frames, but never past the end of a clip
           shorter than the seek itself */
        const at = Math.min(AT_SECONDS, Math.max(0, (video.duration || 0) - 0.1));
        const grab = () => {
          try {
            const w = video.videoWidth;
            const h = video.videoHeight;
            if (!w || !h) { fail(); return; }
            const scale = Math.min(1, MAX_W / w);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(w * scale);
            canvas.height = Math.round(h * scale);
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => { clearTimeout(timer); done(blob || null); }, 'image/jpeg', 0.82);
          } catch {
            fail();
          }
        };
        video.onseeked = grab;
        /* a zero-length or un-seekable clip still gives us the frame we have */
        if (!Number.isFinite(at) || at <= 0) grab(); else { try { video.currentTime = at; } catch { grab(); } }
      };

      url = URL.createObjectURL(file);
      video.src = url;
    } catch {
      done(null);
    }
  });
}

export const isVideoFile = (f) => !!f && /^video\//.test(f.type || '');
